import React, { useState } from "react";
import { ExternalLink, HeartHandshake, Globe, Phone, Flag, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useBookingGate } from "../../lib/bookingGate";

/**
 * BookOnPitchup — reusable affiliate button.
 *
 * IMPORTANT: rendered as a real <a href> tag, not a JS popup. This is the only
 * way to get reliable navigation on mobile browsers (Samsung Internet, in-app
 * browsers, iOS Safari). window.open() with later-redirect pattern is blocked
 * or shows blank pages on many mobile browsers.
 *
 * Strategy:
 * - Build the destination URL client-side from campsite props (pitchupUrl,
 *   websiteUrl, phone). No backend round-trip needed → instant tap-to-open.
 * - Fire-and-forget POST to /affiliate/click for tracking. Doesn't block.
 */

function buildPitchupUrl(pitchupUrl, name, region) {
  if (pitchupUrl) return pitchupUrl;
  // Fallback when site is flagged Pitchup-listed but no direct URL captured
  return `https://www.google.com/search?q=${encodeURIComponent(`site:pitchup.com "${name || ""}"`)}`;
}

function buildSearchUrl(name, region) {
  const cleanRegion = (region || "").toLowerCase() === "united kingdom" ? "" : (region || "");
  const q = `"${name || ""}" ${cleanRegion} campsite UK`.trim();
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

export default function BookOnPitchup({
  campsiteId,
  pitchupListed = undefined,
  pitchupUrl = null,
  pitchupUrlOk = undefined,
  campsiteName = "",
  campsiteRegion = "",
  websiteUrl = null,
  phone = null,
  googleMapsUrl = null,
  tripStart = null,
  tripEnd = null,
  variant = "primary",
  size = "md",
  className = "",
  label = "Find on Pitchup",
  testId = "book-pitchup",
  fullWidth = false,
  showHint = false,
  hintAlign = "center",
  showReport = false,
  /**
   * Hide the inline "Or visit campsite website" fallback link beneath the
   * Pitchup button. Use this when the parent already exposes a dedicated
   * "Book direct on site" CTA (e.g. CampsiteDetail's 4-option booking grid),
   * so the same link doesn't appear twice and dilute the page hierarchy.
   */
  hideWebsiteFallback = false,
}) {
  // A site counts as "on Pitchup" only if it's flagged listed AND the URL
  // hasn't been auto-delisted (2+ users reported it broken) or
  // server-verified dead (404). When broken, fall through to direct
  // website / phone / web-search.
  const isOnPitchup = pitchupListed !== false && pitchupUrlOk !== false;
  const hasWebsite = !!websiteUrl;
  const hasPhone = !!phone;

  const trackClick = () => {
    // Fire-and-forget — don't block navigation. Errors silently ignored.
    if (!campsiteId) return;
    try {
      api.post("/affiliate/click", {
        campsite_id: campsiteId,
        trip_start: tripStart || null,
        trip_end: tripEnd || null,
      }).catch(() => {});
    } catch {}
  };

  // Auth gate: unauthenticated taps on ANY booking link are bounced
  // to /register with a `next=` so they come right back here.
  const gate = useBookingGate();
  const gatedClick = (reason) => (e) => {
    gate(e, { reason });
    if (!e.defaultPrevented) trackClick();
  };

  const base =
    "inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider transition-all no-underline rounded-xl";
  const sizeCls =
    size === "sm" ? "px-3 py-2 text-[11px]" :
    size === "lg" ? "px-6 py-4 text-sm" :
    "px-4 py-3 text-xs";
  const variants = {
    primary:
      "bg-[#ff4500] text-white hover:bg-[#e03d00] shadow-[0_4px_12px_-2px_rgba(255,69,0,0.45)] hover:shadow-[0_6px_18px_-2px_rgba(255,69,0,0.55)] hover:-translate-y-[1px]",
    solid:
      "bg-white text-[#ff4500] border border-[#ff4500] hover:bg-[#fff4ee]",
    link:
      "!px-0 !py-0 bg-transparent text-[#ff4500] hover:text-[#c93700] underline decoration-dotted underline-offset-4 !shadow-none !rounded-none",
  };

  // ---- Pitchup-listed site ----
  if (isOnPitchup) {
    const href = buildPitchupUrl(pitchupUrl, campsiteName, campsiteRegion);
    return (
      <div className={`${fullWidth ? "w-full" : "inline-flex flex-col"} ${className}`}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={gatedClick("book this campsite on Pitchup")}
          className={`${base} ${variants[variant] || variants.primary} ${variant !== "link" ? sizeCls : ""} ${fullWidth ? "w-full" : ""}`}
          data-testid={testId}
        >
          <ExternalLink className="w-4 h-4" />
          <span>{label}</span>
        </a>
        {showHint && (
          <p className={`mt-1.5 text-[10px] mono text-[#888] flex items-center gap-1 ${hintAlign === "center" ? "justify-center" : ""}`}
             title="CampCompass earns a small commission when you book via Pitchup. No extra cost — keeps this app free and ad-free."
             data-testid={`${testId}-hint`}>
            <HeartHandshake className="w-3 h-3 text-[#ff4500]" strokeWidth={2} />
            <span>Supports CampCompass · small commission, no extra cost</span>
          </p>
        )}
        {hasWebsite && !hideWebsiteFallback && (
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer"
             onClick={(e) => gate(e, { reason: "visit this campsite's website" })}
             className={`mt-1.5 text-[11px] mono text-[#0369a1] hover:text-[#075985] underline decoration-dotted underline-offset-4 inline-flex items-center gap-1 ${hintAlign === "center" ? "justify-center" : ""}`}
             data-testid={`${testId}-website`}>
            <Globe className="w-3 h-3" /> Or visit campsite website
          </a>
        )}
        {showReport && campsiteId && (
          <ReportBrokenLink
            campsiteId={campsiteId}
            align={hintAlign}
            testId={`${testId}-report`}
          />
        )}
      </div>
    );
  }

  // ---- Independent site with website ----
  if (hasWebsite) {
    const pitchupBroken = pitchupListed && pitchupUrlOk === false;
    return (
      <div className={`${fullWidth ? "w-full" : "inline-flex flex-col"} ${className}`}>
        {pitchupBroken && (
          <div className={`eyebrow text-[10px] text-[#ff4500] mb-1.5 ${hintAlign === "center" ? "text-center" : ""}`}>
            Not bookable on Pitchup · book direct
          </div>
        )}
        <a href={websiteUrl} target="_blank" rel="noopener noreferrer"
           onClick={gatedClick("book this campsite direct")}
           className={`${base} ${variants[variant] || variants.primary} ${variant !== "link" ? sizeCls : ""} ${fullWidth ? "w-full" : ""}`}
           data-testid={`${testId}-direct`}>
          <Globe className="w-4 h-4" />
          <span>Book on campsite website</span>
        </a>
        {hasPhone && (
          <a href={`tel:${phone.replace(/\s+/g, "")}`}
             onClick={(e) => gate(e, { reason: "call this campsite" })}
             className={`mt-1.5 text-[11px] mono text-[#555] hover:text-[#111] inline-flex items-center gap-1 ${hintAlign === "center" ? "justify-center" : ""}`}
             data-testid={`${testId}-phone-secondary`}>
            <Phone className="w-3 h-3" /> Or call {phone}
          </a>
        )}
      </div>
    );
  }

  // ---- Independent site with phone only ----
  if (hasPhone) {
    const pitchupBroken = pitchupListed && pitchupUrlOk === false;
    return (
      <div className={`${fullWidth ? "w-full" : "inline-flex flex-col"} ${className}`}>
        {pitchupBroken && (
          <div className={`eyebrow text-[10px] text-[#ff4500] mb-1.5 ${hintAlign === "center" ? "text-center" : ""}`}>
            Not bookable on Pitchup · call direct
          </div>
        )}
        <a href={`tel:${phone.replace(/\s+/g, "")}`}
           onClick={gatedClick("call this campsite")}
           className={`${base} ${variants[variant] || variants.primary} ${variant !== "link" ? sizeCls : ""} ${fullWidth ? "w-full" : ""}`}
           data-testid={`${testId}-phone`}>
          <Phone className="w-4 h-4" />
          <span>Call to book · {phone}</span>
        </a>
      </div>
    );
  }

  // ---- Final fallback: open the location on Google Maps if we have coords ----
  if (googleMapsUrl) {
    return (
      <div className={`${fullWidth ? "w-full" : "inline-flex flex-col"} ${className}`}>
        <a href={googleMapsUrl}
           target="_blank" rel="noopener noreferrer"
           onClick={gatedClick("see this location")}
           className={`${base} ${variants[variant] || variants.primary} ${variant !== "link" ? sizeCls : ""} ${fullWidth ? "w-full" : ""}`}
           data-testid={`${testId}-maps`}>
          <ExternalLink className="w-4 h-4" />
          <span>Open in Google Maps</span>
        </a>
        <div className={`text-[10px] mono text-[#888] mt-1.5 ${hintAlign === "center" ? "text-center" : ""}`}>
          No website or phone on record — opens the pitch location.
        </div>
      </div>
    );
  }

  // ---- Last resort: search the web ----
  return (
    <div className={`${fullWidth ? "w-full" : "inline-flex flex-col"} ${className}`}>
      <a href={buildSearchUrl(campsiteName, campsiteRegion)}
         target="_blank" rel="noopener noreferrer"
         onClick={trackClick}
         className={`${base} ${variants[variant] || variants.primary} ${variant !== "link" ? sizeCls : ""} ${fullWidth ? "w-full" : ""}`}
         data-testid={`${testId}-search`}>
        <ExternalLink className="w-4 h-4" />
        <span>Find campsite online</span>
      </a>
    </div>
  );
}

/**
 * Inline "Report broken link" button.
 * Pitchup Cloudflare-blocks our server from verifying URLs, so this is the
 * only reliable signal we have for "not accepting bookings" pages. After 2
 * unique reporters flag the same site, the backend auto-delists it
 * (pitchup_url_ok=false) so it falls to the bottom of sort order.
 */
function ReportBrokenLink({ campsiteId, align = "center", testId = "report-broken" }) {
  const [state, setState] = useState("idle"); // idle | submitting | done

  const submit = async (e) => {
    e.preventDefault();
    if (state !== "idle") return;
    setState("submitting");
    try {
      const { data } = await api.post(`/campsites/${campsiteId}/report-broken-pitchup`, { reason: "not_accepting_bookings" });
      setState("done");
      if (data?.auto_delisted) {
        toast.success("Thanks! Auto-delisted for everyone — refresh to see direct-book option.", { duration: 6000 });
      } else {
        const need = Math.max(0, 2 - (data?.report_count || 0));
        toast.success(`Thanks for flagging. ${need} more report${need === 1 ? "" : "s"} will auto-delist it.`, { duration: 5000 });
      }
    } catch {
      setState("idle");
      toast.error("Couldn't record the report — try again?");
    }
  };

  if (state === "done") {
    return (
      <div className={`mt-2 text-[11px] mono text-[#22863a] inline-flex items-center gap-1.5 ${align === "center" ? "justify-center" : ""}`}
            data-testid={`${testId}-done`}>
        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} /> Reported — thanks for helping the community
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={submit}
      disabled={state === "submitting"}
      className={`mt-2 text-xs font-bold mono text-[#b91c1c] hover:text-white hover:bg-[#b91c1c] border border-[#b91c1c] px-3 py-1.5 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 ${align === "center" ? "self-center" : "self-start"}`}
      data-testid={testId}
    >
      <Flag className="w-3.5 h-3.5" strokeWidth={2.5} />
      {state === "submitting" ? "Reporting…" : 'Got "not accepting bookings"? Report'}
    </button>
  );
}
